import useClassNames from '@medflex/hooks/useClassNames';
import * as PropTypes from 'prop-types';
import * as React from 'react';
import Button from './Button';
import { IconCheckmarkOutline, IconClose, IconExclamationSolid } from './Icons';

// gets either errors.connectionErrors array or errors.messageErrors array
// (if errors.messageErrors buttom={true})
const Alert = ({
  error,
  className,
  showCloseButton,
  onClickCloseButton,
  isRounded,
  isRoundedLg,
}) => {
  const cnWrapper = useClassNames(
    () => [
      'flex',
      'content-center',
      'py-3',
      'pr-8',
      'pl-4',
      'border-t-4',
      'bg-white',
      'text-slate-700',
      'shadow-md',
      error.kind && error.kind === 'alert' && 'border-red-600',
      error.kind && error.kind === 'success' && 'border-green-500',
      error.kind && error.kind === 'warning' && 'border-yellow-400',
      !error.kind && 'border-slate-500',
      isRounded && 'rounded',
      isRoundedLg && 'rounded-lg',
      className,
    ],
    [className],
  );
  const cnIcon = useClassNames(
    () => [
      error.kind && error.kind === 'alert' && 'text-red-600',
      error.kind && error.kind === 'success' && 'text-green-500',
      error.kind && error.kind === 'warning' && 'text-yellow-400',
      !error.kind && 'text-slate-500',
    ],
    [],
  );
  const cnTitle = useClassNames(
    () => [
      'text-xl',
      'font-semibold',
      'leading-none',
      error.kind === 'alert' && 'text-red-600',
      error.kind === 'warning' && 'text-slate-700',
    ],
    [],
  );
  return (
    <div className={cnWrapper}>
      {showCloseButton && (
        <Button
          size="xs"
          shape="circle"
          kind="secondary:ghost"
          className="absolute mt-1 mr-1 top-0 right-0"
          onClick={onClickCloseButton}
        >
          <IconClose size="4" />
        </Button>
      )}
      <div className="flex-0">
        {error.kind && error.kind === 'success' && (
          <IconCheckmarkOutline className={cnIcon} size="6" />
        )}
        {error.kind !== 'success' && <IconExclamationSolid className={cnIcon} size="6" />}
      </div>
      <div className="flex-1 flex flex-col ml-3">
        <h1 className={cnTitle}>{error.title}</h1>
        <p className="leading-relaxed text-sm mt-1 font-normal text-slate-500">{error.text}</p>
      </div>
    </div>
  );
};

const FloatingMessageBar = ({
  className,
  bottom,
  errors,
  showCloseButton = false,
  onClickCloseButton,
  isRounded = false,
  isRoundedLg = false,
}) => {
  const cn = useClassNames(
    () => ['absolute', 'z-50', 'inset-x-0', bottom ? 'bottom-0' : 'top-0', className],
    [className],
  );

  return (
    <div className={cn}>
      {errors.map((error, index) => (
        <Alert
          key={`error-${index}`}
          className={index > 0 && 'mt-4'}
          error={error}
          showCloseButton={showCloseButton}
          onClickCloseButton={onClickCloseButton}
          isRounded={isRounded}
          isRoundedLg={isRoundedLg}
        />
      ))}
    </div>
  );
};

FloatingMessageBar.defaultProps = { onClickCloseButton: () => {} };

FloatingMessageBar.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
  bottom: PropTypes.bool,
  isRounded: PropTypes.bool,
  isRoundedLg: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  onClickCloseButton: PropTypes.func,
  errors: PropTypes.arrayOf(
    PropTypes.shape({
      kind: PropTypes.oneOf(['alert', 'warning', 'success']),
      title: PropTypes.node.isRequired,
      text: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    }),
  ),
};

export default FloatingMessageBar;
